import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Card,
  Group,
  Pagination,
  Table,
  Text,
  Space,
  Title,
  LoadingOverlay,
  TextInput,
  Stack,
  Flex,
  ActionIcon,
  Box,
} from "@mantine/core";
import {
  getCoreRowModel,
  useReactTable,
  getFilteredRowModel,
  flexRender,
  getSortedRowModel,
  getPaginationRowModel,
} from "@tanstack/react-table";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  IconSortAscending,
  IconSortDescending,
  IconPlayerSkipBack,
} from "@tabler/icons-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { PointAssignment } from "./";

const varietyFormatted = {
  1: "song rating",
  2: "survey response",
  3: "check-in",
  10: "correction",
};

export default function PointLedger({
  userLocationId,
  userOrgId,
  actionable = false,
}) {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageCount, setPageCount] = useState(-1);
  const [total, setTotal] = useState(0);
  const [searchValue, setSearchValue] = useState("");
  const [sorting, setSorting] = useState([]);
  const [totalPoints, setTotalPoints] = useState(0);

  const CancelToken = axios.CancelToken;
  const cancelRef = useRef(null);

  const joinData = {
    user_location_id: userLocationId,
    user_organization_id: userOrgId,
  };

  Object.keys(joinData).forEach((k) => {
    if ([null, undefined].includes(joinData[k])) delete joinData[k];
  });

  const adminInfo = useSelector((state) => state.admin);

  const columns = useMemo(
    () =>
      [
        {
          header: () => t("user_app:point_ledger_date_title"),
          accessorKey: "date",
          cell: (info) => (
            <React.Fragment>
              {new Date(info.row.original.created_at).toLocaleString()}
            </React.Fragment>
          ),
        },
        {
          header: () => t("user_app:point_ledger_points_title"),
          accessorKey: "value",
          cell: (info) => info.getValue(),
        },
        {
          header: () => t("user_app:point_ledger_variety_title"),
          accessorKey: "variety",
          id: "variety",
          cell: (info) => (
            <React.Fragment>
              {info.row.original.variety_formatted}
            </React.Fragment>
          ),
        },
        {
          header: () => t("user_app:point_ledger_author_title"),
          accessorKey: "entered_by",
          id: "author",
          cell: (info) => info.getValue(),
        },
        {
          header: () => t("user_app:point_ledger_comments_title"),
          accessorKey: "comment",
          id: "comment",
          cell: (info) => (
            <React.Fragment>
              {info.row.original.rev_referral_date ? (
                <React.Fragment>
                  {t("user_app:reversal_text", {
                    value: Math.abs(info.row.original.value),
                    date: new Date(
                      info.row.original.rev_referral_date
                    ).toLocaleString(),
                  })}
                </React.Fragment>
              ) : (
                <React.Fragment>{info.getValue()}</React.Fragment>
              )}
            </React.Fragment>
          ),
          enableSorting: false,
        },
        {
          header: () => "",
          accessorKey: "",
          id: "actions",
          cell: (info) => (
            <React.Fragment>
              {!info.row.original.reversed && (
                <ReverseButton
                  onSuccess={fetchData}
                  entryId={info.row.original.id}
                />
              )}
            </React.Fragment>
          ),
          enableSorting: false,
        },
      ].filter((f) =>
        !actionable ? !["actions", "author"].includes(f.id) : true
      ),
    []
  );

  const table = useReactTable({
    data: items,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    pageCount,
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
  });

  useEffect(() => {
    if (searchValue.length >= 2) {
      fetchData();
    }
  }, [searchValue]);

  useEffect(() => {
    fetchData();
  }, [userLocationId, userOrgId, JSON.stringify(table.getState())]);

  function fetchData() {
    // setLoading(true);

    const cancel = cancelRef.current;
    if (cancel) cancel();

    const req = {
      ...joinData,
      sorting,
      page_size: table.getState().pagination.pageSize,
      page:
        table.getState().pagination.pageIndex === -1
          ? 0
          : table.getState().pagination.pageIndex,
    };

    if (searchValue) req.search_value = searchValue;

    Object.keys(req).forEach((k) => {
      if ([null, undefined].includes(req[k])) delete req[k];
    });

    if (!Object.keys(req).length) return setLoading(false);

    axios
      .post(`/retrieve-point-ledger/`, req, {
        cancelToken: new CancelToken(function executor(c) {
          cancelRef.current = c;
        }),
      })
      .then(({ data }) => {
        setLoading(false);
        setItems(data.response[0].data);
        setPageCount(data.response[0].page_count);
        if (data.response[0].total !== undefined) {
          setTotal(data.response[0].total);
        } else {
          setTotal(data.response[0].data.length);
        }

        setTotalPoints(data.response[0].point_total);
      })
      .catch((err) => {
        setLoading(false);
        setItems([]);
      });
  }

  return (
    <div>
      {/* <TextInput
        placeholder="Search ledger"
        value={searchValue}
        onChange={(e) => setSearchValue(e.target.value)}
        mb="md"
      /> */}
      <Flex mb="xs" justify="center" direction="column" align="center" gap="xs">
        <Stack gap={0}>
          <Title c="green" align="center" size={64}>
            {totalPoints}
          </Title>
          <Text c="dimmed" align="center" size="xs">
            {t("user_app:total_points_title")}
          </Text>
        </Stack>
        {actionable && (
          <PointAssignment reqData={joinData} fetchData={fetchData} />
        )}
      </Flex>
      <LoadingOverlay visible={loading} />
      <Stack gap="md">
        {table.getRowModel().rows.map(({ original }, index) => {
          const isPositive = original.value > 0;
          const isLastItem = table.getRowModel().rows.length - 1 === index;
          return (
            <Flex
              direction="column"
              key={original.id}
              gap="sm"
              style={{
                borderBottom: isLastItem
                  ? "none"
                  : adminInfo
                  ? "1px solid var(--mantine-color-gray-3)"
                  : "1px solid var(--mantine-color-gray-8)",
                paddingBottom: isLastItem ? 0 : "1rem",
              }}
            >
              <Text fw={500} size="sm" c="dimmed">
                {new Date(original.created_at).toLocaleString()}
              </Text>
              <Flex
                gap="sm"
                align="flex-start"
                direction={{
                  base: "column",
                  sm: "row",
                }}
              >
                <Stack gap={2} style={{ flexGrow: 1 }}>
                  <Text fw={600} size="lg">
                    {original.variety_formatted}
                  </Text>
                  <React.Fragment>
                    {original.rev_referral_date ? (
                      <Text c="dimmed">
                        {t("user_app:reversal_text", {
                          value: Math.abs(original.value),
                          date: new Date(
                            original.rev_referral_date
                          ).toLocaleString(),
                        })}
                      </Text>
                    ) : (
                      <Text c="dimmed">{original.comment}</Text>
                    )}
                  </React.Fragment>
                  {actionable && original.entered_by && (
                    <Text c="dimmed">
                      {t("user_app:entered_by")} {original.entered_by}
                    </Text>
                  )}
                  {actionable && !original.reversed && (
                    <Box mt="xs">
                      <ReverseButton
                        onSuccess={fetchData}
                        entryId={original.id}
                      />
                    </Box>
                  )}
                </Stack>
                <Title
                  hiddenFrom="sm"
                  align="left"
                  order={1}
                  miw={100}
                  c={isPositive ? "green" : "red"}
                >
                  {isPositive ? "+" : ""}
                  {original.value}
                </Title>
                <Title
                  visibleFrom="sm"
                  align="right"
                  order={1}
                  miw={100}
                  c={isPositive ? "green" : "red"}
                >
                  {isPositive ? "+" : ""}
                  {original.value}
                </Title>
              </Flex>
            </Flex>
          );
        })}
      </Stack>
      {pageCount > 1 && (
        <Pagination
          value={table.getState().pagination.pageIndex + 1}
          onChange={(e) => {
            table.setPageIndex(e - 1);
            // table.nextPage();
          }}
          total={pageCount}
          mt="lg"
        />
      )}
    </div>
  );
}

const ReverseButton = ({ entryId, onSuccess }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  function onClick() {
    setLoading(true);

    const req = {
      point_ledger_id: entryId,
    };

    axios
      .post(`/points-ledger-reversal/`, req)
      .then(() => {
        setLoading(false);
        onSuccess();
        toast.success(t("user_app:point_reverse_success_text"));
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err);
      });
  }

  return (
    <ActionIcon
      size="md"
      onClick={onClick}
      loading={loading}
      title="Reverse entry"
      radius="xl"
      variant="light"
      color="red"
    >
      <IconPlayerSkipBack size={16} />
    </ActionIcon>
  );
};

{
  /* <Table striped highlightOnHover>
  <Table.Thead>
    <Table.Tr>
      {table.getFlatHeaders().map((header) => (
        <Table.Th
          key={header.id}
          style={{
            width: header.column.columnDef.width,
          }}
        >
          <div
            {...{
              onClick: header.column.getToggleSortingHandler(),
              style: {
                cursor: header.column.getCanSort() ? "pointer" : "",
              },
            }}
          >
            <Group>
              {flexRender(
                header.column.columnDef.header,
                header.getContext()
              )}
              {{
                asc: <IconSortAscending size={16} />,
                desc: <IconSortDescending size={16} />,
              }[header.column.getIsSorted()] ?? null}
            </Group>
          </div>
        </Table.Th>
      ))}
    </Table.Tr>
  </Table.Thead>
  <Table.Tbody>
    {!loading && table.getRowModel().rows.length === 0 && (
      <Table.Tr>
        <td colSpan="5">No point entries yet</td>
      </Table.Tr>
    )}
    {table.getRowModel().rows.map((row) => (
      <Table.Tr key={row.id}>
        {row.getVisibleCells().map((cell) => (
          <Table.Td
            key={cell.id}
            style={{
              width: `${cell.column.columnDef.minSize}px`,
              // maxWidth: `${cell.column.columnDef.minSize}px`,
            }}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </Table.Td>
        ))}
      </Table.Tr>
    ))}
  </Table.Tbody>
</Table> */
}
